/**
 * The white-on-primary rule, as code (Q-P15, 2026-09-16 — closes KI-03).
 *
 * `packages/ui/src/theme.css` ships two blues: `--color-primary` (#18B3EF) for large
 * decorative fills and identity, and `--color-primary-accessible` (#0A6F9A) for every
 * surface that carries white text and for the brand blue used AS text. Those two values
 * were hand-tuned for HeyAllergy itself.
 *
 * A tenant brands `--color-primary` (FR-UI-03, FR-PLAT-03). If only that token moved, a
 * clinic whose blue is lighter than HeyAllergy's would get a branded primary button with
 * an unreadable label — the exact defect KI-03 closed. So the app derives the clinic's
 * accessible sibling here and writes it into `--color-primary-accessible`, and every
 * component picks it up with no re-render, exactly like the rest of the contract.
 *
 * `contrastRatio` is imported from `@platform/contracts` rather than reimplemented: the
 * API returns the same number to the clinic in `BrandingContrast`, and two
 * implementations of a number a customer is told to trust eventually disagree by a
 * rounding rule (`packages/contracts/src/tenant-settings.ts`).
 *
 * No literal colour lives in this file except `#ffffff`, which is not a design token but
 * the thing being read against — `--color-primary-contrast` is white by definition and
 * is NOT tenant-overridable for this reason.
 */

import { contrastRatio } from '@platform/contracts';

/** WCAG 2.1 §1.4.3 AA for normal text. A button label is normal text. */
export const AA_TEXT = 4.5;

/** WCAG 2.1 §1.4.11 for a UI component's own boundary — a focus ring, a checkbox box. */
export const AA_NON_TEXT = 3;

/** The colour the derived variant is measured against: white, by definition. */
const WHITE = '#ffffff';

/** How much darker the hover state is than the resting fill, as a mix toward black. */
const HOVER_DARKEN = 0.18;

const HEX_LONG = /^#([0-9a-f]{6})$/i;
const HEX_SHORT = /^#([0-9a-f]{3})$/i;

/**
 * `#abc` or `#AABBCC` → the canonical lowercase `#aabbcc`. Null for anything else, so a
 * malformed value from an API or a settings field falls back rather than throwing inside
 * a render.
 */
export function normalizeHex(value: string): string | null {
  const text = value.trim();
  const long = HEX_LONG.exec(text);
  if (long !== null) return `#${long[1]!.toLowerCase()}`;
  const short = HEX_SHORT.exec(text);
  if (short === null) return null;
  const [r = '', g = '', b = ''] = short[1]!.toLowerCase();
  return `#${r}${r}${g}${g}${b}${b}`;
}

/** Contrast of a colour against white, 1 to 21. Null when the hex cannot be parsed. */
export function contrastWithWhite(value: string): number | null {
  const hex = normalizeHex(value);
  return hex === null ? null : contrastRatio(hex, WHITE);
}

/** Mixes a parsed hex toward black. `amount` 0 leaves it alone, 1 makes it black. */
function towardBlack(hex: string, amount: number): string {
  const channel = (offset: number): string =>
    Math.round(Number.parseInt(hex.slice(offset, offset + 2), 16) * (1 - amount))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * The tenant's `--color-primary-accessible`: `color` darkened along its own hue until
 * white text on it clears `minRatio`.
 *
 * Darkening toward black keeps the hue and the saturation the clinic chose and only
 * spends lightness, which is the one dimension contrast is made of — a clinic's blue
 * stays recognisably their blue. A colour that already clears the bar is returned
 * unchanged, so a clinic that picked a dark brand gets exactly what they picked.
 *
 * Binary search rather than a step loop: relative luminance falls monotonically as the
 * mix moves toward black, so twenty-four halvings land within one 0-255 step of the
 * lightest colour that passes, and the guard below walks the rounding error off.
 *
 * Returns null for an unparseable colour — the caller then leaves the token alone and
 * the platform default stays in force.
 */
export function accessibleVariant(color: string, minRatio: number = AA_TEXT): string | null {
  const hex = normalizeHex(color);
  if (hex === null) return null;
  if (contrastRatio(hex, WHITE) >= minRatio) return hex;

  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(towardBlack(hex, mid), WHITE) >= minRatio) hi = mid;
    else lo = mid;
  }

  /* Rounding to whole channels can drop the winner a hundredth under the bar. */
  let amount = hi;
  let out = towardBlack(hex, amount);
  for (let i = 0; i < 256 && contrastRatio(out, WHITE) < minRatio; i += 1) {
    amount = Math.min(1, amount + 1 / 256);
    out = towardBlack(hex, amount);
  }
  return out;
}

/**
 * The hover of an accessible fill: one step darker again, so the press target still
 * moves under the pointer and cannot fall back below the bar it just cleared.
 */
export function accessibleHoverVariant(color: string, minRatio: number = AA_TEXT): string | null {
  const base = accessibleVariant(color, minRatio);
  return base === null ? null : towardBlack(base, HOVER_DARKEN);
}

/**
 * Both values at once, which is how every caller wants them: the pair that a tenant's
 * `primaryColor` turns into. Null when the colour cannot be parsed.
 */
export function accessiblePalette(
  color: string,
  minRatio: number = AA_TEXT,
): { accessible: string; accessibleHover: string } | null {
  const accessible = accessibleVariant(color, minRatio);
  if (accessible === null) return null;
  return { accessible, accessibleHover: towardBlack(accessible, HOVER_DARKEN) };
}
